import { useNavigate, useParams } from 'react-router-dom';
import Accordion from '../../../../components/Accordion/Accordion';
import { AccordionSummaryContentBox } from '../../../../components/Survey/Survey';
import SurveyService, { SurveyTypes } from '../../../../services/SurveyService';
import React, { useEffect, useRef, useState } from 'react';
import SurveyQuestionLocalization from './SurveyQuestionLocalization';
import { ISurveysEntry } from '../../../../shared/interfaces/ISurveysEntry';
import { ICustomSurveyQuestion } from '../../../../shared/interfaces/ICustomSurveyQuestion';
import {
  Box,
  Button,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../languages';
import SurveyUtils from '../../../../shared/SurveyUtils';
import { isCustomSurvey } from '../../../../shared/interfaces/ICustomSurveyEntry';
import SelectLanguagesModal from './SelectLanguagesModal';
import Section from '../../../../components/Main/Section/Section';
import ActionBar from '../../../../components/ActionBar/ActionBar';
import LeftContainer from '../../../../components/Containers/LeftContainer';
import ContentContainer from '../../../../components/Containers/ContentContainer';
import { Add, OpenInNew } from '@mui/icons-material';
import TranslationsService from '../../../../services/TranslationsService';

interface SurveyLocalizationProps {}

const SurveyLocalization: React.FC<SurveyLocalizationProps> = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<ISurveysEntry>();
  const [loading, setLoading] = useState(false);
  const [originLanguage, setOriginLanguage] = useState<keyof typeof LANGUAGES>('de-DE');
  const [targetLanguages, setTargetLanguages] = useState<[keyof typeof LANGUAGES, string][]>([]);
  const [isSurveyStringsExpanded, setIsSurveyStringsExpanded] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<keyof typeof LANGUAGES>('de-DE');
  const [questions, setQuestions] = useState<ICustomSurveyQuestion[]>([]);
  const [surveyDetails, setSurveyDetails] = useState<SurveyTypes>();
  const [t] = useTranslation();
  const [targetLanguageModalOpen, setTargetLanguageModalOpen] = useState(false);

  const getQuestions = async (ls: ISurveysEntry, signal: AbortSignal) => {
    const response = await SurveyService.getQuestions(ls.id, ls.type, setLoading, signal);
    if (response) {
      // set the id so we can use the base item for sorting
      response.forEach((q: ICustomSurveyQuestion) => (q.id = q.questionId!));
      setQuestions(response);
    }
  };

  useEffect(() => {
    if (!surveyDetails) return;
    // @ts-expect-error
    SurveyService.updateCustomsurvey(surveyDetails.id, { ...surveyDetails, lang: originLanguage });
    setSurveyDetails({ ...surveyDetails, lang: originLanguage });
  }, [originLanguage]);

  const refreshQuestionIds = async (ls: ISurveysEntry, signal: AbortSignal) => {
    const response = await SurveyService.getQuestions(ls.id, ls.type, setLoading, signal);
    if (!response) return [];

    for (let x = 0; x < response.length; x++) {
      response[x].questionI18n = questions[x].questionI18n;
      // @ts-ignore
      response[x].id = response[x].questionId;

      setQuestions(response);
    }
    return response;
  };

  const getSurveys = async (signal: AbortSignal) => {
    const response = await SurveyService.getSurvey(setLoading, signal);
    if (response) {
      for (let i = 0; i < response.length; i++) {
        if (response[i].id === params.id) {
          const details = await SurveyService.getSurveyDetails(response[i], setLoading, signal);
          setSurveyDetails(details);
          // @ts-expect-error
          setOriginLanguage(details?.lang ?? 'de-DE');
          setSurvey(response[i]);
          const foundLanguages = Object.keys(details?.i18n ?? {}) as (keyof typeof LANGUAGES)[];

          setTargetLanguages(
            foundLanguages.map((l) => [
              l,
              details?.i18n[l]['__language_display_name__'] ?? LANGUAGES[l].name,
            ])
          );

          setTimeout(() => {
            setCurrentLanguage(foundLanguages[0]);
          }, 0);
          await getQuestions(response[i], signal);
        }
      }
    }
  };

  useEffect(() => {
    console.log('Set current language', currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      await getSurveys(controller.signal);
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const questionDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  function updateOptions(id: string | undefined, idx: number, value: string) {
    if (!survey || !currentLanguage || !id) return;

    setQuestions((prev) =>
      prev.map((q) => {
        if (q.questionId !== id) return q;

        const existingOptions =
          q.questionI18n?.options?.[currentLanguage] ?? q.options.map(() => '');

        const newOptions = [...existingOptions];
        newOptions[idx] = value;

        return {
          ...q,
          questionI18n: {
            ...(q.questionI18n ?? {}),
            options: {
              ...(q.questionI18n?.options ?? {}),
              [currentLanguage]: newOptions,
            },
          },
        };
      })
    );
  }

  function updateRows(id: string | undefined, idx: number, value: string) {
    if (!survey || !currentLanguage || !id) return;

    setQuestions((prev) =>
      prev.map((q) => {
        if (q.questionId !== id) return q;

        const existingRows =
          q.questionI18n?.rows?.[currentLanguage] ?? (q.rows ?? []).map(() => '');

        const newRows = [...existingRows];
        newRows[idx] = value;

        return {
          ...q,
          questionI18n: {
            ...(q.questionI18n ?? {}),
            rows: {
              ...(q.questionI18n?.rows ?? {}),
              [currentLanguage]: newRows,
            },
          },
        };
      })
    );
  }

  function updatePlaceholder(id: string | undefined, value: string) {
    if (!survey || !currentLanguage || !id) return;

    setQuestions((prev) =>
      prev.map((q) =>
        q.questionId === id
          ? {
              ...q,
              questionI18n: {
                ...(q.questionI18n ?? {}),
                placeholder: { ...q.questionI18n.placeholder, [currentLanguage]: value },
              },
            }
          : q
      )
    );
  }

  function updatePrivacyNoticeText(id: string | undefined, value: string) {
    if (!survey || !currentLanguage || !id) return;

    setQuestions((prev) =>
      prev.map((q) =>
        q.questionId === id
          ? {
              ...q,
              questionI18n: {
                ...(q.questionI18n ?? {}),
                PrivacyNoticeText: {
                  ...q.questionI18n.PrivacyNoticeText,
                  [currentLanguage]: value,
                },
              },
            }
          : q
      )
    );
  }

  function updateQuestion(id: string | undefined, value: string) {
    if (!survey || !currentLanguage || !id) return;

    setQuestions((prev) =>
      prev.map((q) =>
        q.questionId === id
          ? { ...q, questionI18n: { ...(q.questionI18n ?? {}), [currentLanguage]: value } }
          : q
      )
    );
  }

  async function clearLanguage(languageToDelete: keyof typeof LANGUAGES) {
    if (!surveyDetails || !currentLanguage) return;

    const newDetails = surveyDetails
      ? (() => {
          const i18n = { ...surveyDetails.i18n };
          delete i18n[languageToDelete];
          return { ...surveyDetails, i18n };
        })()
      : surveyDetails;

    setSurveyDetails(newDetails);

    if (isCustomSurvey(newDetails)) {
      SurveyService.updateCustomsurvey(newDetails.id, newDetails);
    }

    setTargetLanguages((prev) => prev.filter((l) => l[0] !== languageToDelete));
    setCurrentLanguage((prev) => (prev === languageToDelete ? targetLanguages[0][0] : prev));
  }

  function transferLocalization(oldCode: keyof typeof LANGUAGES, newCode: keyof typeof LANGUAGES) {
    if (oldCode === newCode) return;

    setSurveyDetails((prev) => {
      if (!prev) {
        return;
      }

      const i18n = { ...(prev.i18n ?? {}) };
      i18n[newCode] = { ...(i18n[newCode] ?? {}), ...(i18n[oldCode] ?? {}) };
      delete i18n[oldCode];

      return { ...prev, i18n };
    });
  }
  function updateSurvey(key: string, value: string, targetLanguage?: keyof typeof LANGUAGES) {
    let contextLanguage = currentLanguage;
    if (targetLanguage) contextLanguage = targetLanguage;

    if (!surveyDetails || !contextLanguage) return;

    setSurveyDetails((prev) =>
      prev
        ? {
            ...prev,
            i18n: {
              ...prev.i18n,
              [contextLanguage]: {
                ...prev.i18n?.[contextLanguage],
                [key]: value,
              },
            },
          }
        : prev
    );
  }

  function saveQuestion(questionId: string | undefined, localQuestions?: ICustomSurveyQuestion[]) {
    if (!questionId || !survey) return;

    if (!localQuestions) {
      localQuestions = questions;
    }

    const question = localQuestions.find((q) => q.questionId === questionId);
    if (!question) return;

    const questionResult = SurveyService.updateQuestion(survey.type, survey.id, questionId, {
      ...question,
    });
  }

  async function saveSurvey(): Promise<ICustomSurveyQuestion[]> {
    if (!surveyDetails || !currentLanguage) return [];

    if (isCustomSurvey(surveyDetails)) {
      const surveyResult = await SurveyService.updateCustomsurvey(surveyDetails.id, surveyDetails);
      if (survey) {
        return await refreshQuestionIds(survey, new AbortController().signal);
      }
    }
    return [];
  }

  async function saveAllAndReturn() {
    const newQuestions = await saveSurvey();
    newQuestions.forEach((q) => saveQuestion(q.questionId, newQuestions));
    navigate('/research/surveytool/guide');
  }

  const getUntranslatedSurveyField = (fieldName: string) => {
    if (!surveyDetails) return '';

    const fieldValue = (surveyDetails as Record<string, any>)[fieldName];
    if (fieldValue) {
      return fieldValue;
    }
    return '';
  };

  const translateField = async (fieldName: string) => {
    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);
    const res = await TranslationsService.translate(
      getUntranslatedSurveyField(fieldName),
      targetLanguage,
      setLoading,
      controller.signal
    );

    if (res) {
      updateSurvey(fieldName, res);
    }
  };

  const translateAll = async () => {
    if (!survey) return;

    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);

    // Fields
    const surveyFields = SurveyUtils.getTranslatableFieldsByType(survey.type);

    for (const field of surveyFields) {
      await translateField(field.field);
    }

    // Questions
    for (const question of questions) {
      SurveyUtils.addDefaultNpsOptions(question, t);
      // Options
      const res = await TranslationsService.translateBulk(
        question.options.map((option) => option.label),
        targetLanguage,
        setLoading,
        controller.signal
      );

      let index = 0;
      for (const value of res) {
        updateOptions(question.questionId, index++, value);
      }

      if (question.type === 'CUSTOMMATRIX' && question.rows?.length) {
        const translatedRows = await TranslationsService.translateBulk(
          question.rows.map((row) => row.label),
          targetLanguage,
          setLoading,
          controller.signal
        );
        (translatedRows ?? []).forEach((value, index) =>
          updateRows(question.questionId, index, value)
        );
      }

      // Title
      const translatedQuestion = await TranslationsService.translate(
        question.question,
        targetLanguage,
        setLoading,
        controller.signal
      );

      if (translatedQuestion) {
        updateQuestion(question.questionId, translatedQuestion);
      }

      if (question.PrivacyNoticeText) {
        const translatedPrivacyNoticeText = await TranslationsService.translate(
          question.PrivacyNoticeText,
          targetLanguage,
          setLoading,
          controller.signal
        );

        if (translatedPrivacyNoticeText) {
          updatePrivacyNoticeText(question.questionId, translatedPrivacyNoticeText);
        }
      }

      // Placeholder
      if (question.placeholder) {
        const translatedPlaceholder = await TranslationsService.translate(
          question.placeholder,
          targetLanguage,
          setLoading,
          controller.signal
        );

        if (translatedPlaceholder) {
          updatePlaceholder(question.questionId, translatedPlaceholder);
        }
      }
    }
  };

  const getTranslatedSurveyField = (fieldName: string) => {
    if (!surveyDetails || !surveyDetails.i18n || !currentLanguage) return '';

    const fieldValue = surveyDetails.i18n?.[currentLanguage]?.[fieldName];
    if (fieldValue) {
      return fieldValue;
    }
    return '';
  };

  const getFieldLabel = (labelKey: string) => {
    // @ts-expect-error
    return t(labelKey);
  };

  return (
    <Section>
      <ContentContainer sx={{ marginBottom: '1rem' }}>
        <LeftContainer>
          <ActionBar
            children={
              <>
                <Button
                  sx={{ mr: '1rem', px: '2rem' }}
                  variant={'contained'}
                  startIcon={<Add />}
                  onClick={() => setTargetLanguageModalOpen(true)}
                >
                  {t('manage_languages')}
                </Button>
                <InputLabel sx={{ mr: '1rem' }}>{t('main_language')}</InputLabel>
                <Select
                  sx={{ maxHeight: '2.5rem' }}
                  label={t('source_language')}
                  value={originLanguage}
                  onChange={(ev) => setOriginLanguage(ev.target.value as keyof typeof LANGUAGES)}
                >
                  {Object.entries(LANGUAGES)
                    .filter(
                      ([key]) =>
                        targetLanguages
                          .map((tl) => tl[0])
                          .includes(key as keyof typeof LANGUAGES) || key === 'de-DE'
                    )
                    .map(([key, value], idx) => (
                      <MenuItem value={key}>
                        <Box
                          component="img"
                          src={value.flag}
                          alt={value.name}
                          sx={{ mr: '.2rem' }}
                        />
                        <span>{value.name}</span>
                      </MenuItem>
                    ))}
                </Select>
                {targetLanguages.length > 0 && (
                  <>
                    <InputLabel sx={{ ml: '2rem', mr: '1rem' }}>
                      {t('manage_translations')}
                    </InputLabel>
                    <Select
                      sx={{ maxHeight: '2.5rem' }}
                      label={t('target_language')}
                      value={currentLanguage ?? ''}
                      onChange={(ev) =>
                        setCurrentLanguage(ev.target.value as keyof typeof LANGUAGES)
                      }
                    >
                      {targetLanguages.length > 0 ? (
                        Object.entries(LANGUAGES)
                          .filter(([key, _]) => targetLanguages.some((l) => l[0] == key))
                          .map(([key, value], idx) => (
                            <MenuItem value={key}>
                              <Box
                                component="img"
                                src={value.flag}
                                alt={value.name}
                                sx={{ mr: '.2rem' }}
                              />
                              <Tooltip title={key == 'de-CH' ? t('warning_no_translate') : ''}>
                                <span>
                                  {surveyDetails?.i18n?.[key]['__language_display_name__'] ??
                                    value.name}
                                </span>
                              </Tooltip>
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem
                          value={''}
                          disabled={true}
                        >
                          {t('select_at_least_one_language')}
                        </MenuItem>
                      )}
                    </Select>
                  </>
                )}
                {currentLanguage && (
                  <Button
                    sx={{ ml: '2rem', px: '2rem' }}
                    variant={'contained'}
                    onClick={() => translateAll()}
                  >
                    {t('translate_all')}
                  </Button>
                )}
              </>
            }
          />
        </LeftContainer>
      </ContentContainer>
      {survey && surveyDetails && currentLanguage && (
        <Accordion
          summary={
            <AccordionSummaryContentBox>{t('title_intro_outro')}</AccordionSummaryContentBox>
          }
          details={
            <div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '0.5fr 0.5fr 5rem 1fr',
                  gridTemplateRows: 'auto',
                  gap: '1rem',
                }}
              >
                {SurveyUtils.getTranslatableFieldsByType(survey.type).map((field) => (
                  <>
                    <span>{getFieldLabel(field.labelKey)}</span>
                    <Typography style={{ fontStyle: 'italic' }}>
                      {getUntranslatedSurveyField(field.field)}
                    </Typography>
                    <div
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Button
                        sx={{ maxHeight: '36.5px', flexGrow: 1 }}
                        variant={'contained'}
                        onClick={() => translateField(field.field)}
                      >
                        &gt;
                      </Button>
                    </div>
                    <TextField
                      value={getTranslatedSurveyField(field.field)}
                      label={getFieldLabel(field.labelKey)}
                      multiline={true}
                      onChange={(ev) => {
                        updateSurvey(field.field, ev.target.value);
                      }}
                    />
                  </>
                ))}
              </div>
              <Button
                variant={'contained'}
                onClick={() => {
                  saveSurvey();
                  setIsSurveyStringsExpanded(false);
                }}
              >
                Speichern
              </Button>
            </div>
          }
          expanded={isSurveyStringsExpanded}
          onExpandRequest={() => setIsSurveyStringsExpanded((prev) => !prev)}
        />
      )}
      {questions.map((q) => (
        <SurveyQuestionLocalization
          question={q}
          currentLanguage={currentLanguage}
          setQuestionLocalization={updateQuestion}
          setOptionLocalization={updateOptions}
          setRowLocalization={updateRows}
          setPlaceholderLocalization={updatePlaceholder}
          setPrivacyNoticeTextLocalization={updatePrivacyNoticeText}
          onSave={saveQuestion}
        />
      ))}
      <Box sx={{ gap: '16px' }}>
        <Button
          variant={'contained'}
          onClick={() => saveAllAndReturn()}
          sx={{ maxWidth: '200px', mr: '1rem' }}
        >
          {t('finish')}
        </Button>
        <Button
          disabled={!surveyDetails}
          href={
            surveyDetails
              ? `${SurveyUtils.getPublicUrl(surveyDetails.type, surveyDetails.slug)}?preview=true`
              : ''
          }
          target="_blank"
          variant="contained"
          startIcon={<OpenInNew />}
        >
          {t('preview')}
        </Button>
      </Box>
      <SelectLanguagesModal
        open={targetLanguageModalOpen}
        selectedLanguages={targetLanguages}
        onAdd={(newLang: keyof typeof LANGUAGES) => {
          setTargetLanguages((prev) => [...prev, [newLang, LANGUAGES[newLang].name]]);
          updateSurvey('__language_display_name__', LANGUAGES[newLang].name, newLang);
        }}
        onChange={(oldCode: keyof typeof LANGUAGES, code: keyof typeof LANGUAGES) => {
          setTargetLanguages((prev) =>
            prev.map(([langCode, name]) =>
              langCode === oldCode ? [code, LANGUAGES[code].name] : [langCode, name]
            )
          );

          transferLocalization(oldCode, code);
          updateSurvey('__language_display_name__', LANGUAGES[code].name, code);
        }}
        onDelete={(removeLang: keyof typeof LANGUAGES) => clearLanguage(removeLang)}
        onUpdate={(code: string, displayName: string): void => {
          setTargetLanguages((prev) =>
            prev.map(([c, name]) => (c === code ? [c, displayName] : [c, name]))
          );
          updateSurvey('__language_display_name__', displayName, code as keyof typeof LANGUAGES);
        }}
        onClose={function (): void {
          setTargetLanguageModalOpen(false);
        }}
      />
    </Section>
  );
};

export default SurveyLocalization;
